import type { CSSProperties } from 'react';

/**
 * Cyan-tinted letter-mark "logo" used on the integrations index grid and on
 * the slug-page hero pair. We do not redistribute partner logos here —
 * partners ship their own SVGs under their own brand guidelines and we
 * link out to their site instead. This monogram keeps the visual rhythm
 * consistent across all 11 partners without trademark friction.
 */
export function IntegrationLogoMark({
  name,
  size = 56,
  variant = 'partner',
  style,
}: {
  name: string;
  size?: number;
  variant?: 'partner' | 'bizrnr';
  style?: CSSProperties;
}) {
  // Take up to two leading letters from the first one or two words.
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const fontSize = size >= 80 ? size * 0.42 : size * 0.48;

  const base: CSSProperties =
    variant === 'bizrnr'
      ? {
          width: size,
          height: size,
          color: 'var(--paper)',
          background:
            'linear-gradient(135deg, rgba(125,250,255,0.30), rgba(244,244,240,0.10))',
          border: '1px solid rgba(125,250,255,0.32)',
        }
      : {
          width: size,
          height: size,
          color: 'var(--cyan-glow)',
          background:
            'linear-gradient(135deg, rgba(125,250,255,0.20), rgba(14,123,140,0.18))',
          border: '1px solid rgba(125,250,255,0.28)',
        };

  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: size * 0.25,
        fontFamily: 'var(--font-instrument), "Instrument Serif", serif',
        fontStyle: 'italic',
        fontWeight: 400,
        fontSize,
        letterSpacing: '-0.02em',
        flex: 'none',
        ...base,
        ...style,
      }}
    >
      {variant === 'bizrnr' ? 'Br' : letters || '·'}
    </span>
  );
}
